<template>
  <div class="server-services-tab">
    <div class="server-services-tab__card">
      <div class="server-services-tab__card-head">
        <h4>Services</h4>
        <span class="server-services-tab__n">{{ passingLabel }}</span>
      </div>
      <table>
        <thead>
          <tr><th>Unit</th><th>Active state</th><th>Sub state</th><th>Restarts</th><th>Since</th></tr>
        </thead>
        <tbody>
          <tr v-for="svc in services" :key="svc.unit">
            <td><span class="server-services-tab__dot" :class="isFailed(svc) ? 'is-down' : 'is-ok'"></span>{{ svc.unit }}</td>
            <td>{{ svc.active_state }}</td>
            <td>{{ svc.sub_state }}</td>
            <td class="server-services-tab__num">{{ svc.n_restarts }}</td>
            <td>{{ svc.since_seconds ? formatDuration(svc.since_seconds) : '—' }}</td>
          </tr>
          <tr v-if="!services.length">
            <td colspan="5" class="server-services-tab__empty">No service snapshots reported yet.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="server-services-tab__card">
      <div class="server-services-tab__card-head"><h4>Recent transitions</h4></div>
      <ul class="server-services-tab__events">
        <li v-for="event in recentEvents" :key="event.id">
          <strong>{{ event.unit }}</strong>
          {{ event.from_active_state || 'unknown' }} &rarr; {{ event.to_active_state }}
          <span class="server-services-tab__event-time">{{ formatEpoch(event.occurred_at) }}</span>
        </li>
        <li v-if="!recentEvents.length" class="server-services-tab__empty">No transitions recorded yet.</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import { formatEpoch } from '@root/services/serverFormat';

const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
});

const api = useApi();
const services = ref([]);
const recentEvents = ref([]);

const passingLabel = computed(() => {
  if (!services.value.length) {
    return '';
  }
  const passing = services.value.filter((s) => !isFailed(s)).length;
  return `${passing} of ${services.value.length} passing`;
});

function isFailed(svc) {
  return svc.active_state === 'failed';
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

async function load() {
  try {
    const data = await api.get(`${props.apiBase}/${props.serverId}/services`);
    services.value = data.services || [];
    recentEvents.value = data.recent_events || [];
  } catch {
    // Empty state renders either way.
  }
}

onMounted(load);
</script>

<style lang="scss">
.server-services-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__card-head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__n {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    padding: 8px 18px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  td {
    padding: 8px 18px;
    font-size: 12.5px;
    border-top: 1px solid var(--theme-border-soft);
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 8px;

    &.is-ok { background: var(--theme-status-success); }
    &.is-down { background: var(--theme-status-danger); }
  }

  &__num {
    font-variant-numeric: tabular-nums;
  }

  &__empty {
    color: var(--theme-text-tertiary);
    padding: 16px 18px;
    text-align: center;
  }

  &__events {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      padding: 9px 18px;
      border-top: 1px solid var(--theme-border-soft);
      font-size: 12.5px;

      &:first-child {
        border-top: none;
      }
    }
  }

  &__event-time {
    float: right;
    color: var(--theme-text-tertiary);
    font-size: 11px;
    font-family: var(--theme-font-mono);
  }
}
</style>
